import { useState, useEffect } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles, Edit, Plus } from 'lucide-react';
import { API } from './OntologyHelpers';

const ClassDetail = ({ classId, headers, onEdit, onAddIndividual, onGenerateBrief, refreshKey }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/ontology/classes/${classId}`, { headers }); setData(r.data); } catch { /* */ }
    })();
  }, [classId, headers, refreshKey]);
  if (!data) return <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#9CA3AF]" /></div>;
  const props = data.properties || [];
  const indivs = data.individuals || [];
  return (
    <div className="space-y-3" data-testid={`class-detail-${data.class_id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: data.color }} />
            <h3 className="text-base font-semibold text-[#111827]">{data.name}</h3>
            <Badge variant="secondary" className="text-[9px]">v{data.version}</Badge>
          </div>
          <p className="text-[10px] text-[#9CA3AF] mt-1 font-mono">{data.iri}</p>
          {data.description && <p className="text-xs text-[#4B5563] mt-2">{data.description}</p>}
        </div>
        <div className="flex gap-1">
          <Button size="sm" className="h-7 text-xs bg-[#AF52DE] hover:bg-[#8E3FB0]" onClick={onGenerateBrief} data-testid="generate-brief-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1" />Brief
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onEdit} data-testid="edit-class-btn"><Edit className="w-3.5 h-3.5 mr-1" />Edit</Button>
        </div>
      </div>
      <div>
        <h4 className="text-xs font-semibold mb-1.5">Properties ({props.length})</h4>
        {props.length === 0 && <p className="text-[10px] text-[#9CA3AF]">No properties defined.</p>}
        {props.length > 0 && (
          <div className="space-y-1">
            {props.map((p, i) => (
              <div key={i} className="flex items-center gap-2 p-1.5 rounded border border-[#E5E7EB] text-xs">
                <span className="font-mono text-[#002FA7]">{p.name}</span>
                <Badge variant="secondary" className="text-[9px]">{p.datatype}</Badge>
                {p.required && <Badge className="text-[9px] bg-[#FF3B30]/10 text-[#FF3B30]">required</Badge>}
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <h4 className="text-xs font-semibold">Individuals ({data.individual_count})</h4>
          <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={onAddIndividual}><Plus className="w-3 h-3 mr-1" />Add</Button>
        </div>
        {indivs.length === 0 && <p className="text-[10px] text-[#9CA3AF]">No instances yet.</p>}
        {indivs.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {indivs.slice(0, 20).map(i => <Badge key={i.individual_id} variant="secondary" className="text-[10px]">{i.name}</Badge>)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassDetail;
